import { useCallback, useMemo } from 'react';

import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  GetPropsCommonOptions,
  useMultipleSelection,
  UseMultipleSelectionProps,
  UseComboboxGetItemPropsOptions,
  UseComboboxGetInputPropsOptions,
} from 'downshift';

import { useForwardedRef } from '@adsk/alloy-react-helpers';

import { useAutocomplete } from '.';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  wrapGetSelectedItemProps,
} from '../helpers';
import { flattenGroupItemsToDownshift } from '../helpers/groupedItems';
import {
  GenericItem,
  ItemsOrGroupedItems,
  UseMultiAutocompleteProps,
} from '../types';

/**
 * A hook used to manage all logic needed for a multiple select Autocomplete (searchable) dropdown.
 */
const useMultiAutocomplete = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement | HTMLLabelElement,
>({
  items,
  groupedItems,
  selectedItems: selectedItemsProp,
  itemToString = defaultLabelGetter,
  multipleSelectionStateReducer,
  stateReducer: stateReducerProp,
  target,
  inputRef: inputRefProp,
  isItemDisabled = defaultIsItemDisabled,
  ...props
}: UseMultiAutocompleteProps<TItem, TTarget>) => {
  const maybeGroupedItems = useMemo(
    () => items || groupedItems || [],
    [groupedItems, items],
  );
  checkReferenceEquality({
    items: maybeGroupedItems,
    ...props,
    selectedItems: selectedItemsProp,
  });

  const targetRef = useForwardedRef(target || null);
  const inputRef = useForwardedRef(inputRefProp || null);

  const useMultipleSelectionArgs: UseMultipleSelectionProps<TItem> = {};
  if (selectedItemsProp) {
    // Setting this to undefined results in "cannot get length of undefined", so do this hack
    useMultipleSelectionArgs.selectedItems = selectedItemsProp;
  }

  if (multipleSelectionStateReducer) {
    // Setting this to undefined results in "stateReducer is not a function"
    useMultipleSelectionArgs.stateReducer = multipleSelectionStateReducer;
  }

  const {
    getDropdownProps,
    addSelectedItem: rawAddSelectedItem,
    removeSelectedItem: rawRemoveSelectedItem,
    getSelectedItemProps: rawGetSelectedItemProps,
    ...useMultipleSelectionReturnValue
  } = useMultipleSelection<TItem>({
    ...useMultipleSelectionArgs,
    // Setting initial active index blocks downshift from focusing input on mount
    initialActiveIndex: 0,
    ...props,
  });

  const flatItems = useMemo(
    () => flattenGroupItemsToDownshift(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const _isItemDisabled = useCallback(
    (item: TItem) => isItemDisabled(item, flatItems.indexOf(item)),
    [flatItems, isItemDisabled],
  );

  const addSelectedItem = useCallback(
    (item: TItem) => {
      if (_isItemDisabled(item)) {
        return;
      }

      rawAddSelectedItem(item);
    },
    [_isItemDisabled, rawAddSelectedItem],
  );

  const removeSelectedItem = useCallback(
    (item: TItem) => {
      if (_isItemDisabled(item)) {
        return;
      }

      rawRemoveSelectedItem(item);
    },
    [_isItemDisabled, rawRemoveSelectedItem],
  );

  const autocompleteStateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      { changes, type }: UseComboboxStateChangeOptions<TItem>,
    ): Partial<UseComboboxState<TItem>> => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          const isActiveElementChildOfControl = targetRef.current?.contains(
            document.activeElement,
          );

          const overrides: Partial<UseComboboxState<TItem>> = {};

          if (isActiveElementChildOfControl) {
            overrides.isOpen = true; // keep menu open if we're clicking within control
          } else {
            overrides.inputValue = ''; // remove user entered input value if we're clicking outside control
          }

          return {
            ...changes,
            ...overrides,
          };
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          return {
            ...changes,
            inputValue: '', // remove user entered input value on selection or menu close
          };
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          return {
            ...changes,
            isOpen: true, // keep menu open after selection
            highlightedIndex: state.highlightedIndex, // maintain highlighted item after selection
            inputValue: '', // reset user entered input value after selection
          };
        default:
          return changes;
      }
    },
    [targetRef],
  );

  const onStateChange = useCallback(
    ({ type, selectedItem }: UseComboboxStateChange<TItem>) => {
      if (type === useCombobox.stateChangeTypes.InputKeyDownEnter) {
        const isSelected =
          selectedItem &&
          useMultipleSelectionReturnValue.selectedItems.includes(selectedItem);
        if (selectedItem && !isSelected) {
          addSelectedItem(selectedItem);
        }
        if (selectedItem && isSelected) {
          removeSelectedItem(selectedItem);
        }
      }
    },
    [
      addSelectedItem,
      removeSelectedItem,
      useMultipleSelectionReturnValue.selectedItems,
    ],
  );

  const stateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      actionAndChanges: UseComboboxStateChangeOptions<TItem>,
    ) => {
      const updatedState = autocompleteStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [autocompleteStateReducer, stateReducerProp],
  );

  // workaround, the implicit XOR type gets lost causing type errors.  Casting it here makes it explicit
  const itemArgs = { items, groupedItems } as ItemsOrGroupedItems<TItem>;

  const {
    isOpen,
    getItemProps: rawGetItemProps,
    getInputProps: rawGetInputProps,
    controlProps: allControlProps,
    menuProps: allMenuProps,
    ...useAutocompleteReturnValue
  } = useAutocomplete({
    ...itemArgs,
    itemToString,
    selectedItem: null,
    target: targetRef,
    stateReducer,
    onStateChange,
    isItemDisabled,
    ...props,
  });

  const { selectedItem: _ignored1, ...controlProps } = allControlProps;
  const { selectedItem: _ignored2, ...menuProps } = allMenuProps;

  const getItemProps = useCallback(
    ({
      item,
      isSelected: isSelectedProp,
      ...itemProps
    }: UseComboboxGetItemPropsOptions<TItem>) => {
      const isSelected =
        isSelectedProp ||
        useMultipleSelectionReturnValue.selectedItems.includes(item);

      const toReturn = rawGetItemProps({ item, isSelected, ...itemProps });

      return {
        ...toReturn,
        onClick: () => {
          if (_isItemDisabled(item)) {
            return;
          }
          isSelected ? removeSelectedItem(item) : addSelectedItem(item);
        },
        isMultiple: true,
      };
    },
    [
      addSelectedItem,
      _isItemDisabled,
      rawGetItemProps,
      removeSelectedItem,
      useMultipleSelectionReturnValue.selectedItems,
    ],
  );

  const getInputProps = useCallback(
    (
      options?: UseComboboxGetInputPropsOptions,
      otherOptions?: GetPropsCommonOptions,
    ) => {
      const dropdownProps = getDropdownProps(
        { ref: inputRef, preventKeyAction: isOpen },
        otherOptions,
      );

      return rawGetInputProps(
        {
          ...dropdownProps,
          ...options,
        },
        otherOptions,
      );
    },
    [getDropdownProps, inputRef, isOpen, rawGetInputProps],
  );

  const getSelectedItemProps: typeof rawGetSelectedItemProps = useCallback(
    (args) =>
      wrapGetSelectedItemProps({
        getSelectedItemProps: rawGetSelectedItemProps,
        isItemDisabled,
      })(args),
    [isItemDisabled, rawGetSelectedItemProps],
  );

  return {
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    isOpen,
    /**
     * @deprecated use value returned from menuProps
     */
    addSelectedItem,
    /**
     * @deprecated use value returned from controlProps or menuProps
     */
    removeSelectedItem,
    /**
     * @deprecated use value returned from menuProps
     */
    getItemProps,
    /**
     * @deprecated use value returned from controlProps
     */
    getInputProps,
    /**
     * @deprecated use value returned from controlProps
     */
    getSelectedItemProps,
    controlProps: {
      ...controlProps,
      getInputProps,
      selectedItems: useMultipleSelectionReturnValue.selectedItems,
      removeSelectedItem,
      getSelectedItemProps,
    },
    menuProps: {
      ...menuProps,
      getItemProps,
      selectedItems: useMultipleSelectionReturnValue.selectedItems,
      addSelectedItem,
      removeSelectedItem,
    },
    ...useMultipleSelectionReturnValue,
    ...useAutocompleteReturnValue,
  };
};

export default Object.assign(useMultiAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes,
  multipleSelectionStateChangeTypes: useMultipleSelection.stateChangeTypes,
});
